import axios from 'axios';
import BaseService from './BaseService';

class LocaleService extends BaseService {
    /**
     * Fetch locale file for a specific language and namespace
     * @param {string} language - Language code (e.g., 'en', 'fr')
     * @param {string} namespace - Namespace (e.g., 'common', 'assessment')
     * @returns {Promise<Object>} Locale data
     */
    async fetchLocale(language, namespace) {
        try {
            // Load from API endpoint to avoid CloudFront cache delays
            const response = await this.get(`/languages/${language}/${namespace}.json`);
            return response;
        } catch (e) {
            console.error(`Error fetching locale ${language}/${namespace}:`, e);
            throw e;
        }
    }

    /**
     * Save locale changes to the server
     * @param {Object} changes - Changes grouped by namespace and language
     * @returns {Promise<Object>} Response from server
     */
    async saveLocaleChanges(changes) {
        try {
            // This will call your API endpoint to save the changes
            const response = await this.post('/languages/translations', changes);
            return response;
        } catch (e) {
            console.error('Error saving locale changes:', e);
            throw e;
        }
    }

    /**
     * Translate text using AI
     * @param {Array<string>} emptyLanguages - Array of language codes that need translation
     * @param {Object} currentValue - Object with lang and value properties
     * @returns {Promise<Object>} Translations object with language codes as keys
     */
    async translateText(emptyLanguages, currentValue) {
        try {
            const response = await this.post('/languages/translate', {
                emptyLanguages,
                currentValue
            });
            return response;
        } catch (e) {
            console.error('Error translating text:', e);
            throw e;
        }
    }

    /**
     * Get available languages
     * @returns {Array} List of available languages
     */
    getAvailableLanguages() {
        return [
            { code: 'en', name: 'English' },
            { code: 'ar_KW', name: 'Arabic (Kuwait)' },
            { code: 'bs_BA', name: 'Bosnian (Bosnia)' },
            { code: 'cs', name: 'Czech' },
            { code: 'da', name: 'Danish' },
            { code: 'de', name: 'German' },
            { code: 'el', name: 'Greek' },
            { code: 'es', name: 'Spanish' },
            { code: 'es_419', name: 'Spanish (Latin America)' },
            { code: 'es_ES', name: 'Spanish (Spain)' },
            { code: 'fi', name: 'Finnish' },
            { code: 'fil', name: 'Filipino' },
            { code: 'fr', name: 'French' },
            { code: 'fr-CA', name: 'French (Canada)' },
            { code: 'fr_CA', name: 'French (Canada)' },
            { code: 'is', name: 'Icelandic' },
            { code: 'it', name: 'Italian' },
            { code: 'ja', name: 'Japanese' },
            { code: 'kl', name: 'Greenlandic' },
            { code: 'ko', name: 'Korean' },
            { code: 'nl', name: 'Dutch' },
            { code: 'nn', name: 'Norwegian Nynorsk' },
            { code: 'no', name: 'Norwegian' },
            { code: 'pl', name: 'Polish' },
            { code: 'pt_BR', name: 'Portuguese (Brazil)' },
            { code: 'pt_PT', name: 'Portuguese (Portugal)' },
            { code: 'ru', name: 'Russian' },
            { code: 'sv', name: 'Swedish' },
            { code: 'vi', name: 'Vietnamese' },
            { code: 'zh_CN', name: 'Chinese (Simplified)' },
        ];
    }

    /**
     * Get available namespaces
     * @returns {Array} List of available namespaces
     */
    getAvailableNamespaces() {
        return ['common', 'assessment'];
    }
}

export default new LocaleService();

